import {
  createContext, useContext, useEffect, useState, type ReactNode, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { auth, functions } from './firebase';

export interface OrgSummary {
  orgId: string;
  /**
   * Whatever the membership document says - including 'manager', which this
   * union had never been told about, and any CUSTOM role id.
   *
   * Widened to a plain string on purpose. Nothing in the UI should switch on
   * this value; ask `capabilities` instead. A narrow union here invited exactly
   * the kind of `role === 'owner' || role === 'admin'` check that made a custom
   * role invisible to the interface while the server happily honoured it.
   */
  role: string;
  name: string;
  slug: string;
  status: string;
  /**
   * What this person may actually do here, resolved by the server against this
   * org's role overrides and custom roles.
   *
   * Absent from an older cached response, so treat missing as "nothing" rather
   * than "everything" - a UI that shows too much is a support call, a UI that
   * shows too little is a shrug.
   */
  capabilities?: string[];
  /**
   * This org demands a second factor.
   *
   * Missing means NOT required - an older cached response must not lock people
   * out of a dashboard whose policy it cannot see.
   */
  requireMfa?: boolean;
}

interface OrgState {
  orgs: OrgSummary[];
  current: OrgSummary | null;
  loading: boolean;
  error: string | null;
  setCurrent: (orgId: string) => void;
  reload: () => Promise<void>;
}

/**
 * Ask what the signed-in person may do in the current org.
 *
 *   const can = useCan();
 *   {can('branding.manage') && <BrandingPanel />}
 *
 * THIS IS FOR DISPLAY ONLY. Every callable checks the same capability again on
 * the server; hiding a button is a courtesy, not a control. The reason it
 * exists is that eleven panels were being handed one boolean meaning "is this
 * person an owner or an admin", so permission to hide a machine also unlocked
 * branding, the web address, the team list and two-factor policy.
 */
export function useCan(): (cap: string) => boolean {
  const { current } = useOrg();
  const caps = useMemo(() => new Set(current?.capabilities ?? []), [current]);
  return (cap: string) => caps.has(cap);
}

const Ctx = createContext<OrgState | null>(null);
const LAST_ORG_KEY = 'vtgbackup-last-org';

/**
 * Which organisation the signed-in user is looking at.
 *
 * The list comes from the `myOrgs` callable rather than a Firestore query,
 * because the org documents themselves are only readable once you are already
 * known to be a member — a chicken-and-egg the callable resolves by reading the
 * user's custom claims server-side.
 *
 * Everything that touches data hangs off `current.orgId`, so there is exactly
 * one place that decides which tenant is in view.
 */
export function OrgProvider({ children }: { children: ReactNode }) {
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [current, setCur] = useState<OrgSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      // A user whose role was just granted still holds a token minted before
      // the claim existed. Force a refresh so myOrgs sees the new membership.
      await auth.currentUser?.getIdToken(true);
      let list = (await httpsCallable<unknown, { orgs: OrgSummary[] }>(functions, 'myOrgs')({}))
        .data?.orgs ?? [];

      // Empty can mean "genuinely no memberships" OR "claims were never minted".
      // Re-derive from the membership documents and retry once before believing it.
      if (list.length === 0) {
        try {
          await httpsCallable(functions, 'refreshMyClaims')({});
          await auth.currentUser?.getIdToken(true);
          list = (await httpsCallable<unknown, { orgs: OrgSummary[] }>(functions, 'myOrgs')({}))
            .data?.orgs ?? [];
        } catch {
          // Fall through with the empty list — NoOrg explains the state.
        }
      }
      setOrgs(list);

      const remembered = (() => {
        try { return localStorage.getItem(LAST_ORG_KEY); } catch { return null; }
      })();
      setCur(list.find((o) => o.orgId === remembered) ?? list[0] ?? null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your organisations.');
      setOrgs([]);
      setCur(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, []);

  const setCurrent = (orgId: string) => {
    const next = orgs.find((o) => o.orgId === orgId);
    if (!next) return;
    setCur(next);
    try { localStorage.setItem(LAST_ORG_KEY, orgId); } catch { /* ignore */ }
  };

  return (
    <Ctx.Provider value={{ orgs, current, loading, error, setCurrent, reload: load }}>
      {children}
    </Ctx.Provider>
  );
}

export function useOrg(): OrgState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useOrg must be used inside <OrgProvider>');
  return v;
}

/**
 * Shown when a signed-in user belongs to no organisation.
 *
 * This is a real state, not an error: an account can exist before anyone has
 * granted it a role. Saying so plainly beats an empty dashboard that looks
 * broken.
 */
export function NoOrg({ email, onSignOut, onRetry }: {
  email: string | null; onSignOut: () => void; onRetry: () => void;
}) {
  return (
    <div className="signin-wrap">
      <div className="signin-card">
        <div className="brand-name">No organisation yet</div>
        <p className="panel-note" style={{ marginTop: 4 }}>
          {email ?? 'Your account'} is signed in but isn’t a member of any organisation, so there is
          nothing to show. Ask an administrator to add you, then reload.
        </p>
        <button className="btn btn-primary" onClick={onRetry}>Check again</button>
        <button className="btn" onClick={onSignOut}>Sign out</button>
      </div>
    </div>
  );
}
